// ============================================================
// Warehouse export — the flat row every sink writes
//
// One stable, documented schema for S3 files (NDJSON / Parquet), BigQuery
// and Snowflake. Bump EXPORT_SCHEMA_VERSION only with additive changes.
// docs/warehouse-exports.md lists the columns.
// ============================================================
export const EXPORT_SCHEMA_VERSION = 1;
const dimCols = [1, 2, 3, 4].map(i => ({
    name: `d${i}`,
    type: 'string',
    description: `custom dimension slot ${i} (project config: dimensions)`,
}));
const metricCols = Array.from({ length: 12 }, (_, i) => ({
    name: `m${i + 1}`,
    type: 'float',
    description: `custom metric slot ${i + 1} (project config: metrics)`,
}));
export const WAREHOUSE_COLUMNS = [
    { name: 'event_id', type: 'string', description: 'event id (stable across retries)' },
    { name: 'project_id', type: 'string', description: 'YOM project id' },
    { name: 'sub_tenant', type: 'string', description: "sub-tenant external id ('' = none)" },
    { name: 'event', type: 'string', description: 'event name (GA4-aligned taxonomy)' },
    { name: 'event_timestamp', type: 'timestamp', description: 'event time (UTC)' },
    { name: 'received_at', type: 'timestamp', description: 'time YOM received it (UTC)' },
    { name: 'host', type: 'string', description: 'page host' },
    { name: 'path', type: 'string', description: 'page path' },
    { name: 'referrer', type: 'string', description: 'referrer host' },
    { name: 'channel', type: 'string', description: 'default channel group' },
    { name: 'utm_source', type: 'string', description: 'utm_source' },
    { name: 'utm_medium', type: 'string', description: 'utm_medium' },
    { name: 'utm_campaign', type: 'string', description: 'utm_campaign' },
    { name: 'utm_term', type: 'string', description: 'utm_term' },
    { name: 'utm_content', type: 'string', description: 'utm_content' },
    { name: 'country', type: 'string', description: 'ISO country' },
    { name: 'region', type: 'string', description: 'region' },
    { name: 'city', type: 'string', description: 'city' },
    { name: 'device', type: 'string', description: 'desktop | mobile | tablet | unknown' },
    { name: 'browser', type: 'string', description: 'browser family' },
    { name: 'os', type: 'string', description: 'OS family' },
    { name: 'session_id', type: 'string', description: 'session id' },
    { name: 'visitor_id', type: 'string', description: "visitor id ('' when cookieless)" },
    { name: 'user_id', type: 'string', description: 'user id (consented or server-side only)' },
    { name: 'item_id', type: 'string', description: 'object the event is about' },
    { name: 'label', type: 'string', description: 'event-specific detail' },
    ...dimCols,
    { name: 'value', type: 'float', description: 'monetary or numeric value' },
    { name: 'currency', type: 'string', description: 'ISO currency of value' },
    { name: 'qty', type: 'float', description: 'quantity' },
    { name: 'duration_ms', type: 'float', description: 'duration in ms' },
    ...metricCols,
    { name: 'consent', type: 'string', description: 'granted | denied | unknown' },
    { name: 'internal', type: 'bool', description: 'internal / test traffic' },
    { name: 'source', type: 'string', description: 'client | server' },
    { name: 'ctx', type: 'json', description: 'envelope custom context (JSON object)' },
    { name: 'params', type: 'json', description: 'event params after PII scrubbing (JSON)' },
    { name: 'items', type: 'json', description: 'ecommerce items (JSON array) or null' },
];
export function toExportRow(ev) {
    const row = {
        event_id: ev.id,
        project_id: ev.projectId,
        sub_tenant: ev.subTenant ?? '',
        event: ev.event,
        event_timestamp: new Date(ev.ts).toISOString(),
        received_at: new Date(ev.received ?? ev.ts).toISOString(),
        host: ev.host ?? '',
        path: ev.path ?? '',
        referrer: ev.referrer ?? '',
        channel: ev.channel ?? '',
        utm_source: ev.utm?.source ?? '',
        utm_medium: ev.utm?.medium ?? '',
        utm_campaign: ev.utm?.campaign ?? '',
        utm_term: ev.utm?.term ?? '',
        utm_content: ev.utm?.content ?? '',
        country: ev.country ?? '',
        region: ev.region ?? '',
        city: ev.city ?? '',
        device: ev.device ?? 'unknown',
        browser: ev.browser ?? '',
        os: ev.os ?? '',
        session_id: ev.session ?? '',
        visitor_id: ev.visitor ?? '',
        user_id: ev.userId ?? '',
        item_id: ev.itemId ?? '',
        label: ev.label ?? '',
    };
    for (let i = 0; i < 4; i++) {
        row[`d${i + 1}`] = ev.dims?.[i] ?? '';
    }
    row.value = Number(ev.value ?? 0);
    row.currency = ev.currency ?? '';
    row.qty = Number(ev.qty ?? 0);
    row.duration_ms = Number(ev.durationMs ?? 0);
    for (let i = 0; i < 12; i++) {
        row[`m${i + 1}`] = Number(ev.metrics?.[i] ?? 0);
    }
    row.consent = ev.consent ?? 'unknown';
    row.internal = ev.internal === true;
    row.source = ev.source ?? 'client';
    row.ctx = JSON.stringify(ev.ctx ?? {});
    row.params = JSON.stringify(ev.params ?? {});
    row.items = ev.items && ev.items.length > 0 ? JSON.stringify(ev.items) : null;
    return row;
}
/** Parse one archived NDJSON object (`v2/<tenant>/<project>/<date>/<hour>/*.ndjson`). */
export function parseArchiveObject(text) {
    const out = [];
    for (const line of text.split('\n')) {
        if (!line.trim()) {
            continue;
        }
        try {
            out.push(JSON.parse(line));
        }
        catch {
            // A torn line can only come from a manual edit; skip it rather than block the export.
        }
    }
    return out;
}
// --- Warehouse type mappings ---
export function bigQuerySchema() {
    const t = {
        string: 'STRING',
        float: 'FLOAT',
        bool: 'BOOLEAN',
        timestamp: 'TIMESTAMP',
        json: 'STRING',
    };
    return WAREHOUSE_COLUMNS.map(c => ({
        name: c.name,
        type: t[c.type],
        mode: c.name === 'items' ? 'NULLABLE' : c.name === 'event_id' ? 'REQUIRED' : 'NULLABLE',
        description: c.description,
    }));
}
export function snowflakeType(t) {
    return {
        string: 'VARCHAR',
        float: 'DOUBLE',
        bool: 'BOOLEAN',
        timestamp: 'TIMESTAMP_TZ',
        json: 'VARIANT',
    }[t];
}
//# sourceMappingURL=rows.js.map